import { useMemo } from "react";
import ContentCard from "@/components/UI/Cards/ContentCard";
import StatCard from "@/components/UI/Cards/StatCard";
import { TablePlot } from "@/views/TrialHomeScreen/components/TablePlots";
import type { TableRow } from "../issuesTableConfig";
import { buildTrialIssuesAnalytics } from "./utils";

type TrialIssuesAnalyticsProps = {
  issuesData: TableRow[];
  rulesData: TableRow[];
};

export default function TrialIssuesAnalytics({
  issuesData,
  rulesData,
}: TrialIssuesAnalyticsProps) {
  const analytics = useMemo(
    () => buildTrialIssuesAnalytics(issuesData, rulesData),
    [issuesData, rulesData],
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Total Issues"
          value={analytics.totalIssues}
          description="Current trial issue records in this dataset"
        />
        <StatCard
          label="Impacted Subjects"
          value={analytics.distinctSubjects}
          description="Distinct subjects referenced by issues"
        />
        <StatCard
          label="Impacted Sites"
          value={analytics.distinctSites}
          description="Distinct sites represented in issue records"
        />
        <StatCard
          label="Average Age"
          value={`${analytics.avgDaysOpen}d`}
          description="Mean number of days issues have remained open"
        />
        <StatCard
          label="Active Rules"
          value={analytics.distinctRules}
          description="Distinct rule identifiers available in the rules dataset"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ContentCard
          title="Issues By Study Status"
          subtitle="Shows where issue volume is concentrated across subject lifecycle states."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byStudyStatus]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Status"
          />
        </ContentCard>
        <ContentCard
          title="Issues By Source System"
          subtitle="Highlights the systems and source combinations creating the most issue volume."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.bySource]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Source"
          />
        </ContentCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ContentCard
          title="Issue Aging"
          subtitle="Buckets open issues by age to make stale work easier to spot."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.ageBuckets]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Age"
            sorted={false}
          />
        </ContentCard>
        <ContentCard
          title="Assignee Workload"
          subtitle="Compares issue ownership across assignees in the current mock dataset."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byAssignee]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Assignee"
          />
        </ContentCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ContentCard
          title="Issue Categories"
          subtitle="Surfaces the most frequent discrepancy categories affecting issue volume."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.byCategory]}
            mode="counts"
            preDefinedStacks={["Issues"]}
            stackLabels={{ Issues: "Issues" }}
            categoriesTitle="Category"
          />
        </ContentCard>
        <ContentCard
          title="Rules By Source System"
          subtitle="Shows which upstream systems contribute the most configured discrepancy rules."
          contentClassName="min-h-[280px]"
        >
          <TablePlot
            data={[analytics.rulesBySource]}
            mode="counts"
            preDefinedStacks={["Rules"]}
            stackLabels={{ Rules: "Rules" }}
            categoriesTitle="Source"
          />
        </ContentCard>
      </div>
    </div>
  );
}
